import React, { useState } from "react";
import { Card, SectionLabel, Button, Input, Modal } from "./ui";
import { useApp } from "../context/AppContext";
import api from "../lib/api";
import { Download, ShieldCheck, Trash2, AlertTriangle, Loader2 } from "lucide-react";

const L = (lang, fr, en) => (lang === "fr" ? fr : en);

export default function AccountDataCard() {
  const { lang, user, toast } = useApp();
  const [exporting, setExporting] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState("");
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    setExporting(true);
    try {
      const res = await api.get("/account/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cockpit-os-export-${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast(L(lang, "Export téléchargé", "Export downloaded"));
    } catch {
      toast(L(lang, "Échec de l'export", "Export failed"));
    } finally {
      setExporting(false);
    }
  };

  const emailMatches = confirmEmail.trim().toLowerCase() === (user?.email || "").toLowerCase();

  const deleteAccount = async () => {
    if (!emailMatches) return;
    setDeleting(true);
    try {
      await api.delete("/account", { data: { confirm_email: confirmEmail.trim() } });
      toast(L(lang, "Compte supprimé", "Account deleted"));
      window.location.href = "/login";
    } catch (e) {
      const msg = e?.response?.data?.detail?.message || L(lang, "Échec de la suppression", "Deletion failed");
      toast(msg);
      setDeleting(false);
    }
  };

  return (
    <Card className="mb-6" data-testid="account-data-card">
      <div className="flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-text-secondary" />
        <SectionLabel className="!mb-0">{L(lang, "Données & confidentialité", "Data & privacy")}</SectionLabel>
      </div>
      <p className="text-text-secondary text-sm mt-1.5 mb-4">
        {L(lang,
          "Vos données vous appartiennent. Exportez-les à tout moment, ou supprimez définitivement votre compte.",
          "Your data belongs to you. Export it anytime, or permanently delete your account.")}
      </p>

      <button onClick={exportData} disabled={exporting} data-testid="export-data-btn"
        className="flex items-center justify-between w-full px-3.5 py-3 rounded-xl border border-[var(--border)] text-sm hover:bg-surface-hover transition-colors">
        <span className="flex items-center gap-2 text-text-primary">
          {exporting ? <Loader2 className="w-4 h-4 spin text-text-secondary" /> : <Download className="w-4 h-4 text-text-secondary" />}
          {L(lang, "Exporter toutes mes données (ZIP)", "Export all my data (ZIP)")}
        </span>
        <span className="text-xs text-text-secondary">{L(lang, "JSON + fichiers", "JSON + files")}</span>
      </button>

      <div className="mt-4 rounded-xl border border-red-300/50 bg-red-500/[0.03] p-4">
        <div className="flex items-center gap-2 text-red-500 mb-1">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-semibold">{L(lang, "Zone de danger", "Danger zone")}</span>
        </div>
        <p className="text-xs text-text-secondary mb-3">
          {L(lang,
            "La suppression de votre compte efface définitivement toutes vos données et fichiers. Cette action est irréversible. Pensez à exporter vos données avant.",
            "Deleting your account permanently erases all your data and files. This cannot be undone. Consider exporting your data first.")}
        </p>
        <Button variant="secondary" onClick={() => { setConfirmEmail(""); setShowDelete(true); }}
          data-testid="open-delete-account-btn"
          className="!border-red-300 !text-red-500 hover:!bg-red-500/10">
          <Trash2 className="w-4 h-4 mr-2" />{L(lang, "Supprimer mon compte", "Delete my account")}
        </Button>
      </div>

      <Modal open={showDelete} onClose={() => !deleting && setShowDelete(false)}
        title={L(lang, "Supprimer définitivement le compte", "Permanently delete account")}
        footer={<>
          <Button variant="secondary" onClick={() => setShowDelete(false)} disabled={deleting}>{L(lang, "Annuler", "Cancel")}</Button>
          <Button onClick={deleteAccount} disabled={!emailMatches || deleting} data-testid="confirm-delete-account-btn"
            className="!bg-red-500 hover:!bg-red-600">
            {deleting ? L(lang, "Suppression…", "Deleting…") : L(lang, "Supprimer définitivement", "Delete permanently")}
          </Button>
        </>}>
        <div className="space-y-3">
          <div className="flex items-start gap-2 rounded-lg bg-red-500/[0.06] border border-red-300/40 p-3">
            <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
            <p className="text-sm text-text-primary">
              {L(lang, "Toutes vos entités, espaces, tâches, documents et fichiers seront effacés définitivement.",
                       "All your entities, spaces, tasks, documents and files will be permanently erased.")}
            </p>
          </div>
          <Button variant="secondary" onClick={exportData} disabled={exporting} data-testid="export-before-delete-btn" className="w-full">
            <Download className="w-4 h-4 mr-2" />{L(lang, "Exporter mes données d'abord", "Export my data first")}
          </Button>
          <div>
            <label className="text-xs text-text-secondary block mb-1.5">
              {L(lang, "Tapez votre e-mail pour confirmer", "Type your email to confirm")} : <span className="text-text-primary font-medium">{user?.email}</span>
            </label>
            <Input value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)}
              placeholder={user?.email} data-testid="delete-confirm-email-input" autoComplete="off" />
          </div>
        </div>
      </Modal>
    </Card>
  );
}
